<script setup lang="ts">
import { ConfirmationPopup, useMobileView } from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useGetCreatorDetail, useGetCreatorExtraInfo, useGetPaymentSummary, useUpdateCreator, useZezamLogin } from '@/api'
import ArrowIcon from '@/assets/icons/arrow.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import MetapicIcon from '@/assets/icons/metapic-logo-white.svg?component'
import {
  CreatorAffiliateLinks,
  CreatorCampaignsAndAdvertisers,
  CreatorDetailHeader,
  CreatorDetailInfo,
  CreatorReport,
} from '@/components/CreatorDetail'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { serializeStatusAction } from '@/lib/adapters/creator-detail.adapter'
import { FLAG_CREATOR_REPORT } from '@/lib/constants/flags.constants'
import { config, notification } from '@/lib/helpers'
import { useBackToPreviousPage } from '@/lib/hooks/back-to-previous-page.hooks'
import type { CreatorStatusAction } from '@/lib/types/creator-detail.types'
import {
  BoxComponent,
  ButtonComponent,
  DrawerComponent,
  FlexComponent,
  LoadingComponent,
  TabsComponent,
  TypographyComponent,
} from '@/ui-library'

const route = useRoute()
const creatorId = computed(() => Number(route.params.id as string))
const handleBack = useBackToPreviousPage({ name: 'creators' })

const { data, isFetching, error, getCreator } = useGetCreatorDetail(creatorId)
const { data: extraInfo, getExtraInfo } = useGetCreatorExtraInfo(creatorId)
const { data: paymentSummary } = useGetPaymentSummary(creatorId)
const { updateCreator, isLoading: isUpdatingStatus } = useUpdateCreator()
const {
  loginOrCreate: zezamLoginOrCreate,
  confirmCreation: zezamConfirmCreation,
  needsCreation: zezamNeedsCreation,
  isLoading: isZezamLoading,
} = useZezamLogin()

const { isMobileView } = useMobileView()
const isCreatorReportEnabled = useFlag(FLAG_CREATOR_REPORT)

const isIframeDrawerOpen = ref(false)
const iframeUrl = ref('')
const iframeTitle = ref('')

const isDataMissing = computed(() => !data.value)

const tabs = computed(() => {
  const id = creatorId.value
  const legacyBase = config.legacyAdminAppUrl
  return [
    { label: 'Info', path: 'info' },
    { label: 'Links', path: 'links' },
    { label: 'Campaigns & advertisers', path: 'campaigns' },
    { label: 'Payments', path: 'payments', href: `${legacyBase}/#/creators/${id}` },
    { label: 'Invalid clicks report', path: 'cheat-reports', href: `${legacyBase}/#/creators/${id}` },
    { label: 'Content', path: 'content', href: `/content-library?creatorId=${id}` },
    ...(isCreatorReportEnabled.value ? [{ label: 'Creator Report', path: 'creator-report' }] : []),
  ]
})

const onStatusChange = async (action: CreatorStatusAction) => {
  if (!data.value) {
    return
  }
  const payload = serializeStatusAction(action)
  const success = await updateCreator(data.value.id, payload)
  if (!success) {
    return
  }
  notification.success('Status updated successfully')
  await getCreator(true)
}

const onZezamClick = async () => {
  if (!data.value) {
    return
  }
  await zezamLoginOrCreate(data.value.id)
}

const onZezamConfirm = async () => {
  if (!data.value) {
    return
  }
  await zezamConfirmCreation(data.value.id)
}

const onRefresh = () => {
  void getCreator(true)
  void getExtraInfo()
}
</script>
<template>
  <HeaderComponent title="Creator details">
    <template #backButton>
      <ButtonComponent :is-icon="true" @click="handleBack">
        <el-icon color="var(--inverted)" class="icon">
          <ArrowIcon />
        </el-icon>
      </ButtonComponent>
    </template>
    <FlexComponent :gap="8">
      <ButtonComponent
        variant="transparent"
        :is-disabled="isDataMissing"
        :href="isDataMissing ? undefined : `${config.legacyAdminAppUrl}/#/creators/${creatorId}/edit`"
      >
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <EditIcon />
          </el-icon>
        </template>
        Edit
      </ButtonComponent>
      <ButtonComponent variant="transparent" :is-disabled="isDataMissing" :is-loading="isZezamLoading" @click="onZezamClick">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <MetapicIcon />
          </el-icon>
        </template>
        View My Page
      </ButtonComponent>
    </FlexComponent>
  </HeaderComponent>

  <LoadingComponent v-if="isFetching" :size="64" :is-full-size="true" />
  <template v-else-if="!error && data">
    <BoxComponent :p="32" class="container">
      <BoxComponent :mb="32">
        <CreatorDetailHeader
          :creator="data"
          :current-balance="paymentSummary?.currentBalance"
          :upcoming-payment="paymentSummary?.upcomingPayment"
          :is-updating="isUpdatingStatus"
          @status-change="onStatusChange"
        />
      </BoxComponent>
      <BoxComponent :mb="32">
        <TabsComponent :tabs="tabs" :has-dynamic-urls="true">
          <template #tab1>
            <CreatorDetailInfo :creator="data" :extra-info="extraInfo" @refresh="onRefresh" />
          </template>

          <template #tab2>
            <CreatorAffiliateLinks :user-id="data.id" />
          </template>

          <template #tab3>
            <CreatorCampaignsAndAdvertisers v-if="route.params.tab === 'campaigns'" :user-id="data.id" />
          </template>

          <template v-if="isCreatorReportEnabled" #tab7>
            <CreatorReport :user-id="data.id" />
          </template>
        </TabsComponent>
      </BoxComponent>
    </BoxComponent>
  </template>
  <ConfirmationPopup
    v-model="zezamNeedsCreation"
    :is-loading="isZezamLoading"
    title="Create page"
    description="This will create a blank page with a random slug for this user. Continue?"
    submit-btn-text="Create"
    submit-btn-variant="primary"
    @on-submit="onZezamConfirm"
  />
  <DrawerComponent v-model="isIframeDrawerOpen" :with-header="true" :size="isMobileView ? '100%' : '80%'" :is-resizable="!isMobileView">
    <template #header>
      <TypographyComponent variant="headline-small">
        {{ iframeTitle }}
      </TypographyComponent>
    </template>
    <iframe v-if="isIframeDrawerOpen" :src="iframeUrl" class="iframe-content" frameborder="0" allow="clipboard-read; clipboard-write" />
  </DrawerComponent>
</template>
<style scoped lang="scss">
:deep(.icon) {
  path {
    fill: currentColor;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.iframe-content {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
